import { gql } from '@apollo/client';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CloseIcon, CopyIcon, RegenerateIcon, SsoUploadIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { SsoValidationStatus } from '~graphql/__generated__/types';
import { OrganizationSsoIndexPageDocument } from '~pages/[organization]/settings/sso/__generated__/Index.graphql';
import { useSamlGenerateEntityIdMutation } from './__generated__/ChooseSsoServiceModal.graphql';
import {
  usePublishSamlMutation,
  useSaveSamlProviderMutation,
} from './__generated__/SamlRegistrationModal.graphql';

const redirectUrl = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/login/callback`;

// Types for form data
export interface SamlFormData {
  connectionName: string;
  entityId: string;
  signingUrl: string;
  x509Certificate: string;
}

export interface SamlInitialData {
  connectionName?: string;
  entityId: string;
  signingUrl?: string;
  x509Certificate?: string;
  validationStatus?: SsoValidationStatus | null;
}

// Schema definition for SAML
const SamlSchema = z.object({
  connectionName: z.string(),
  entityId: z.string(),
  signingUrl: z.string().refine((val) => val === '' || z.string().url().safeParse(val).success, {
    message: 'Invalid url',
  }),
  x509Certificate: z.string(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ssoId: string;
  initialData?: SamlInitialData;
  isEditing?: boolean;
  domain: string;
}

export function SamlRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
  ssoId,
  initialData,
  isEditing = false,
  domain,
}: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <SamlRegistrationModalContent
          ssoId={ssoId}
          onClose={onClose}
          onSuccess={onSuccess}
          initialFocusRef={initialFocusRef}
          initialData={initialData}
          isEditing={isEditing}
          domain={domain}
        />
      </ModalContent>
    </Modal>
  );
}

interface ContentProps {
  ssoId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: SamlInitialData;
  isEditing?: boolean;
  domain: string;
}

function SamlRegistrationModalContent({
  ssoId,
  initialFocusRef,
  onClose,
  onSuccess,
  initialData,
  isEditing = false,
  domain,
}: ContentProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Determine if we should start in read-only mode
  const hasValidationStatus = initialData?.validationStatus != null;
  const [isEditMode, setIsEditMode] = useState(!hasValidationStatus);

  const [saveSaml] = useSaveSamlProviderMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });
  const [publishSaml] = usePublishSamlMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<SamlFormData>({
    defaultValues: {
      connectionName: initialData?.connectionName ?? '',
      entityId: initialData?.entityId ?? '',
      signingUrl: initialData?.signingUrl ?? '',
      x509Certificate: initialData?.x509Certificate ?? '',
    },
    mode: 'onChange',
    resolver: zodResolver(SamlSchema),
  });

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        connectionName: initialData.connectionName ?? domain ?? '',
        signingUrl: initialData.signingUrl ?? '',
        x509Certificate: initialData.x509Certificate ?? '',
      });
      // If there's existing certificate data, show a placeholder filename
      if (initialData.x509Certificate) {
        setFileName(t('certificateUploaded'));
      }
    }
  }, [initialData, reset, domain]);

  // Watch form values
  const connectionName = watch('connectionName');
  const entityId = watch('entityId');
  const signingUrl = watch('signingUrl');
  const x509Certificate = watch('x509Certificate');

  // Derived state
  const isReadOnly = hasValidationStatus && !isEditMode;

  // Submit requires all fields to have values
  const canSubmit = (() => {
    if (!isValid) return false;
    if (isReadOnly) return false;
    const hasConnectionName = connectionName && connectionName.trim().length > 0;
    const hasEntityId = entityId && entityId.trim().length > 0;
    const hasSigningUrl = signingUrl && signingUrl.trim().length > 0;
    const hasCertificate = x509Certificate && x509Certificate.trim().length > 0;
    return hasConnectionName && hasEntityId && hasSigningUrl && hasCertificate;
  })();

  // Handle Edit button click
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Handle Regenerate Entity ID
  const handleRegenerate = async () => {
    try {
      const { data } = await regenerateEntityId({ variables: { id: ssoId } });
      const options = data?.ssoProviderSamlGenerateEntityId.provider?.options;
      if (options?.__typename !== 'SamlOptions' || !options.entityId) {
        throw new Error('Failed to generate new Entity ID');
      }

      setValue('entityId', options.entityId, { shouldDirty: true, shouldValidate: true });
    } catch (e) {
      toast({
        title: t('ssoProviderNameGenerationFailed'),
        status: 'error',
      });
    }
  };

  // Handle Save (draft - upsert only, no publish)
  const handleSave = useCallback(async () => {
    const data = getValues();
    setIsSaving(true);
    try {
      await saveSaml({
        variables: {
          input: {
            id: ssoId,
            displayName: data.connectionName,
            signInEndpoint: data.signingUrl,
            signingCert: data.x509Certificate,
          },
        },
      });

      toast({
        title: t('ssoSavedAsDraft'),
        description: t('ssoSavedAsDraftDescription'),
        status: 'success',
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: t('errorSavingSso'),
        description: (e as Error).message,
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [getValues, saveSaml, ssoId, toast, onSuccess, onClose]);

  const [regenerateEntityId, { loading: loadingRegeneration }] = useSamlGenerateEntityIdMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  // Handle Submit (upsert + publish)
  const handleFormSubmit = useCallback(
    async (data: SamlFormData) => {
      try {
        // First save the data
        await saveSaml({
          variables: {
            input: {
              id: ssoId,
              displayName: data.connectionName,
              signInEndpoint: data.signingUrl,
              signingCert: data.x509Certificate,
            },
          },
          refetchQueries: [],
          awaitRefetchQueries: false,
        });

        // Then publish
        await publishSaml({
          variables: { id: ssoId },
        });

        toast({
          title: t('ssoSubmitted'),
          description: t('ssoSubmittedDescription'),
          status: 'success',
        });
        onSuccess?.();
        onClose();
      } catch (e) {
        toast({
          title: t('errorSubmittingSso'),
          description: (e as Error).message,
          status: 'error',
        });
      }
    },
    [saveSaml, publishSaml, ssoId, toast, onSuccess, onClose],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setValue('x509Certificate', content, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsText(file);
    }
  };

  const handleBrowseClick = () => {
    if (!isReadOnly) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = () => {
    setFileName('');
    setValue('x509Certificate', '', { shouldDirty: true, shouldValidate: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalHeader>{isEditing ? t('editSsoRegistration') : t('ssoRegistration')}</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <Stack spacing={4}>
          <Text fontSize="sm" color="gray.600">
            {t('fillSsoRegistrationDetails')}
          </Text>

          {/* Connection Name */}
          <FormControl isRequired isInvalid={Boolean(errors.connectionName)}>
            <FormLabel fontWeight="semibold">{t('connectionName')}</FormLabel>
            <Input
              {...register('connectionName')}
              ref={(e) => {
                register('connectionName').ref(e);
                initialFocusRef.current = e;
              }}
              isReadOnly={isReadOnly}
              bg={isReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.connectionName?.message}</FormErrorMessage>
          </FormControl>

          {/* Entity ID */}
          <FormControl isRequired isInvalid={Boolean(errors.entityId)}>
            <FormLabel fontWeight="semibold">{t('entityId')}</FormLabel>
            <HStack spacing={0} alignItems="stretch">
              <InputGroup flex={1}>
                <Input
                  {...register('entityId')}
                  isReadOnly={true}
                  bg={isReadOnly ? 'gray.50' : 'white'}
                  borderRightRadius={!hasValidationStatus ? 0 : undefined}
                  textOverflow="ellipsis"
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy Entity ID"
                    icon={<CopyIcon boxSize={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => {
                      const entityId = getValues('entityId');
                      navigator.clipboard.writeText(entityId);
                      toast({ title: t('copiedToClipboard'), status: 'info', duration: 1000 });
                    }}
                  />
                </InputRightElement>
              </InputGroup>
              {!hasValidationStatus && (
                <Button
                  size="md"
                  minH="40px"
                  variant="unstyled"
                  display="flex"
                  alignItems="center"
                  bg="gray.50"
                  color="gray.700"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="inherit"
                  borderLeftWidth={0}
                  borderLeftRadius={0}
                  borderRightRadius="md"
                  _hover={{ bg: 'gray.100' }}
                  onClick={handleRegenerate}
                  isDisabled={hasValidationStatus}
                  isLoading={loadingRegeneration}
                  rightIcon={<RegenerateIcon boxSize={4} />}
                  whiteSpace="nowrap"
                  px={4}
                  fontWeight="medium"
                >
                  {t('regenerate')}
                </Button>
              )}
            </HStack>
          </FormControl>

          {/* Redirect URL (read-only) */}
          <FormControl>
            <FormLabel fontWeight="semibold">{t('redirectUrl')}</FormLabel>
            <InputGroup>
              <Input value={redirectUrl} isReadOnly bg="gray.50" />
              <InputRightElement>
                <IconButton
                  aria-label="Copy Redirect URL"
                  icon={<CopyIcon boxSize={4} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    navigator.clipboard.writeText(redirectUrl);
                    toast({ title: t('copiedToClipboard'), status: 'info', duration: 1000 });
                  }}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {/* Entity ID + Redirect URL info: only in edit mode */}
          {!isReadOnly && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{t('ssoSamlEntityIdWarning')}</Text>
            </Alert>
          )}

          {/* Signing URL */}
          <FormControl isRequired isInvalid={Boolean(errors.signingUrl)}>
            <FormLabel fontWeight="semibold">{t('signingUrl')}</FormLabel>
            <Input
              {...register('signingUrl')}
              isDisabled={isReadOnly}
              isReadOnly={isReadOnly}
              bg={isReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.signingUrl?.message}</FormErrorMessage>
          </FormControl>

          {/* X509 Certificate */}
          <FormControl isRequired isInvalid={Boolean(errors.x509Certificate)}>
            <FormLabel fontWeight="semibold">{t('x509SigningCertificate')}</FormLabel>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pem,.crt,.cer,.cert"
              style={{ display: 'none' }}
              disabled={isReadOnly}
            />

            {/* Show filename with remove button after upload */}
            {fileName ? (
              <HStack spacing={2} py={4} justify="center">
                <Text fontSize="sm" color="gray.700">
                  {fileName}
                </Text>
                {!isReadOnly && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={handleRemoveFile}
                    aria-label="Remove file"
                    p={0}
                    minW="auto"
                  >
                    <CloseIcon boxSize={4} />
                  </Button>
                )}
              </HStack>
            ) : (
              /* Show upload UI when no file selected */
              <Box
                border="1px dashed"
                borderColor={isReadOnly ? 'gray.200' : 'gray.300'}
                borderRadius="md"
                p={6}
                textAlign="center"
                cursor={!isReadOnly ? 'pointer' : 'not-allowed'}
                onClick={handleBrowseClick}
                _hover={!isReadOnly ? { borderColor: 'blue.400', bg: 'gray.50' } : {}}
                transition="all 0.2s"
                opacity={!isReadOnly ? 1 : 0.5}
              >
                <VStack spacing={2}>
                  <Text fontSize="md" color="gray.900">
                    {t('ssoSamlUploadX509Certificate')}
                  </Text>
                  <Text fontSize="sm" color="gray.200">
                    {t('ssoSamlAcceptedCertificateFormat')}
                  </Text>
                  <Button
                    size="sm"
                    variant="solid"
                    backgroundColor="blue.700"
                    textColor="white"
                    isDisabled={isReadOnly}
                    leftIcon={<SsoUploadIcon boxSize={4} />}
                  >
                    {t('ssoSamlUploadCertficate')}
                  </Button>
                </VStack>
              </Box>
            )}
            <FormErrorMessage>{errors.x509Certificate?.message}</FormErrorMessage>
          </FormControl>

          {/* Warning message: only in edit mode */}
          {!isReadOnly && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{t('ssoSamlEditWarning')}</Text>
            </Alert>
          )}
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="ghost"
          colorScheme="gray"
          onClick={onClose}
          isDisabled={isSubmitting || isSaving}
        >
          {t('cancel')}
        </Button>

        {isReadOnly ? (
          /* Read-only mode: Show Edit button */
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            marginLeft="3"
            onClick={handleEditClick}
          >
            {t('edit')}
          </Button>
        ) : (
          /* Edit mode: Show Save button */
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            marginLeft="3"
            onClick={handleSave}
            isDisabled={!isDirty || isSubmitting || isSaving || hasValidationStatus}
            isLoading={isSaving}
          >
            {t('save')}
          </Button>
        )}

        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={!canSubmit || isSubmitting || isSaving}
          isLoading={isSubmitting}
        >
          {t('submit')}
        </Button>
      </ModalFooter>
    </form>
  );
}

// GraphQL mutations
SamlRegistrationModal.graphql = {
  mutations: {
    SaveSamlProvider: gql`
      mutation SaveSamlProvider($input: SsoProviderSamlUpsertInput!) {
        ssoProviderSamlUpsert(input: $input) {
          displayName
          validationStatus
          strategy
          options {
            entityId
            signInEndpoint
            signingCert
          }
        }
      }
    `,
    PublishSaml: gql`
      mutation PublishSaml($id: ID!) {
        ssoProviderSamlPublish(input: { id: $id }) {
          id
          domain
          verificationStatus
          provider {
            ... on SamlProvider {
              displayName
              validationStatus
              strategy
            }
          }
        }
      }
    `,
  },
};
